import { Link } from "react-router-dom";
import { ArrowRight, Target, Eye, Handshake, UserCircle, Users, Trophy, CurrencyDollar, Globe } from "@phosphor-icons/react";
import { useEffect } from "react";
import SEO, { aboutPageSchema, breadcrumbSchema } from "@/components/SEO";

const ABOUT_BG = "https://images.unsplash.com/photo-1768121496378-0644c37e7fc7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBidWlsZGluZyUyMGFyY2hpdGVjdHVyZSUyMGRhcmt8ZW58MHx8fHwxNzc0OTM5NjYxfDA&ixlib=rb-4.1.0&q=85";

const aboutMetrics = [
  { icon: Users, value: "140+", label: "Clients Advised" },
  { icon: CurrencyDollar, value: "$36M+", label: "Assets Under Advisory" },
  { icon: Trophy, value: "4", label: "Industry Awards" },
  { icon: Globe, value: "100%", label: "Crypto-Focused" },
];

const values = [
  {
    icon: Target,
    title: "Knowledgeable",
    desc: "Experienced, informed, proactive. We stay ahead of crypto regulation, blockchain technology, and market developments so our clients can focus on what matters to them."
  },
  {
    icon: Handshake,
    title: "Trustworthy",
    desc: "Reliable, transparent, ethical. We do not make promises we cannot keep. We do not recommend services or products we do not believe in. Every recommendation is in our client's interest."
  },
  {
    icon: Eye,
    title: "Empowering",
    desc: "Motivating, supportive, and capability-building. Our goal is not to create dependency. It is to build our clients' confidence and knowledge so they can make better crypto decisions independently."
  }
];

const team = [
  {
    name: "Founding Team",
    role: "Advisory & Strategy",
    desc: "Deep expertise across crypto markets, Indian regulation, and blockchain technology. Our founders bring decades of combined experience in finance, legal consulting, and software engineering."
  },
  {
    name: "Tax & Compliance",
    role: "Regulatory Advisory",
    desc: "Specialists in Indian crypto taxation under Section 115BBH, FIU-IND registration, PMLA compliance, and cross-border regulatory frameworks for NRIs and global businesses."
  },
  {
    name: "Technology",
    role: "Development & Architecture",
    desc: "Blockchain developers, smart contract auditors, and technical architects with hands-on experience across Ethereum, Solana, Polygon, and other major ecosystems."
  }
];

export default function About() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="about-page">
      <SEO
        title="About BitGenix | India's Trusted Crypto Consultancy"
        description="BitGenix is India's leading crypto consultancy for individuals and businesses. 140+ clients advised, $36M+ in assets, 4 industry awards. Expert advisory on crypto tax, compliance, investment, and blockchain development."
        path="/about"
        keywords={[
          "about BitGenix",
          "crypto consultancy India",
          "crypto tax and compliance experts",
          "blockchain consulting team India",
          "crypto investment advisory firm",
        ]}
        jsonLd={[
          aboutPageSchema(),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "About", path: "/about" },
          ]),
        ]}
      />
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src={ABOUT_BG}
            alt="BitGenix crypto consultancy team"
            className="w-full h-full object-cover"
            decoding="async"
          />
          <div className="absolute inset-0 bg-[#21324D]/92" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-32">
          <div className="max-w-2xl">
            <p className="font-body text-sm uppercase tracking-[0.2em] text-[#A7A55C] mb-6">About BitGenix</p>
            <h1
              className="font-heading font-semibold text-4xl sm:text-5xl lg:text-6xl text-[#F5F5F2] leading-none tracking-tight mb-8"
              data-testid="about-heading"
            >
              One firm for everything crypto
            </h1>
            <p className="font-body text-lg text-[#F5F5F2]/75 leading-relaxed max-w-xl">
              BitGenix exists because the crypto industry is fragmented. Investors juggle multiple firms for tax, investment, and security advice. Businesses juggle separate vendors for licensing, development, and compliance. We consolidate everything under one roof.
            </p>
          </div>
        </div>
      </section>

      {/* Metrics */}
      <section className="bg-[#152F2C] py-16">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {aboutMetrics.map((m) => (
              <div key={m.label} className="text-center">
                <m.icon size={24} weight="duotone" className="text-[#A7A55C]/60 mx-auto mb-3" />
                <p className="font-heading font-semibold text-3xl sm:text-4xl text-[#A7A55C] tracking-tight">{m.value}</p>
                <p className="font-body text-xs text-[#F5F5F2]/50 mt-1 uppercase tracking-[0.12em]">{m.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-24 sm:py-32 bg-[#F5F5F2]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-5">
              <p className="font-body text-sm uppercase tracking-[0.15em] text-[#A7A55C] font-medium mb-4">Our Mission</p>
              <h2 className="font-heading font-semibold text-3xl sm:text-4xl text-[#21324D] tracking-tight leading-tight">
                Be the single trusted partner for your entire crypto journey
              </h2>
            </div>
            <div className="lg:col-span-7">
              <p className="font-body text-base text-[#383A46] leading-[1.8] mb-6">
                Crypto is legal in India, but the landscape is complex. The government imposes a 30% flat tax on crypto gains under Section 115BBH, requires 1% TDS on every transaction under Section 194S, mandates FIU-IND registration for all VDA service providers, and enforces PMLA compliance across the industry. Globally, regulations differ by jurisdiction and change constantly.
              </p>
              <p className="font-body text-base text-[#383A46] leading-[1.8] mb-6">
                Individual crypto investors lose money to emotional decisions, missed tax obligations, and poor security practices. Businesses burn time and capital navigating licensing requirements, hiring specialized blockchain talent, and managing complex crypto projects without experienced guidance.
              </p>
              <p className="font-body text-base text-[#383A46] leading-[1.8]">
                Nobody bundles individual crypto advisory and business advisory under one brand. CA firms handle tax. Development shops build products. Legal firms manage compliance. BitGenix does all of it as your consultants. We are not lawyers, financial advisors, or fund managers. We are the experienced partners who simplify the entire crypto landscape for you.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-2xl mb-16">
            <p className="font-body text-sm uppercase tracking-[0.15em] text-[#A7A55C] font-medium mb-4">Our Values</p>
            <h2 className="font-heading font-semibold text-3xl sm:text-4xl text-[#21324D] tracking-tight leading-tight">
              What drives our crypto consultancy
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {values.map((value) => (
              <div key={value.title} className="border border-[#383A46]/10 p-8 sm:p-10 hover:border-[#A7A55C]/30 transition-colors duration-300">
                <div className="bg-[#21324D] w-12 h-12 flex items-center justify-center mb-6">
                  <value.icon size={22} weight="duotone" className="text-[#A7A55C]" />
                </div>
                <h3 className="font-body font-medium text-xl text-[#21324D] mb-3">
                  {value.title}
                </h3>
                <p className="font-body text-sm text-[#383A46]/70 leading-relaxed">
                  {value.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-24 sm:py-32 bg-[#F5F5F2]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-2xl mb-16">
            <p className="font-body text-sm uppercase tracking-[0.15em] text-[#A7A55C] font-medium mb-4">Our Team</p>
            <h2 className="font-heading font-semibold text-3xl sm:text-4xl text-[#21324D] tracking-tight leading-tight">
              The people behind BitGenix
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {team.map((member) => (
              <div key={member.name} className="bg-white border border-[#383A46]/10 p-8 hover:border-[#A7A55C]/30 transition-colors duration-300">
                <div className="w-16 h-16 bg-[#21324D] flex items-center justify-center mb-6">
                  <UserCircle size={28} weight="duotone" className="text-[#A7A55C]" />
                </div>
                <h3 className="font-body font-medium text-lg text-[#21324D] mb-1">{member.name}</h3>
                <p className="font-body text-sm text-[#A7A55C] mb-4">{member.role}</p>
                <p className="font-body text-sm text-[#383A46]/70 leading-relaxed">{member.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 sm:py-32 bg-[#21324D]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <h2 className="font-heading font-semibold text-3xl sm:text-4xl text-[#F5F5F2] tracking-tight leading-tight mb-6">
            Work with India's trusted crypto consultancy
          </h2>
          <p className="font-body text-base text-[#F5F5F2]/65 max-w-xl mx-auto mb-10">
            Ready to simplify your crypto journey? Start with a free consultation. No obligations, no pressure.
          </p>
          <Link
            to="/contact"
            className="bg-[#A7A55C] text-[#F5F5F2] hover:bg-[#8C8A4A] transition-all duration-300 font-body font-semibold text-sm tracking-wide px-8 py-4 rounded-none inline-flex items-center gap-2 hover:gap-3"
            data-testid="about-cta-button"
          >
            Book a Consultation <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </section>
    </div>
  );
}
